import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Email as EmailIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const EmailConfig = () => {
  const [loading, setLoading] = useState(false);
  const [configData, setConfigData] = useState({
    smtp_host: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: '',
    use_tls: true,
    provider: 'smtp',
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/api/config/email');
      if (response.data) {
        setConfigData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch email config:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setConfigData({
      ...configData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/api/config/email', configData);
      toast.success('Email configuration saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email configuration');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    setLoading(true);
    try {
      await api.post('/api/config/email/test', {
        recipient: configData.smtp_username,
      });
      toast.success('Test email sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <EmailIcon color="primary" />
        <Typography variant="h6">Email Configuration</Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Email Provider</InputLabel>
              <Select
                name="provider"
                value={configData.provider}
                onChange={handleChange}
                label="Email Provider"
              >
                <MenuItem value="smtp">SMTP (Custom)</MenuItem>
                <MenuItem value="gmail">Gmail</MenuItem>
                <MenuItem value="sendgrid">SendGrid</MenuItem>
                <MenuItem value="mailgun">Mailgun</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="SMTP Host"
              name="smtp_host"
              value={configData.smtp_host}
              onChange={handleChange}
              required
              helperText="e.g., smtp.gmail.com"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="SMTP Port"
              name="smtp_port"
              type="number"
              value={configData.smtp_port}
              onChange={handleChange}
              required
              helperText="Usually 587 for TLS, 465 for SSL"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="SMTP Username"
              name="smtp_username"
              value={configData.smtp_username}
              onChange={handleChange}
              required
              helperText="Email address or username"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="SMTP Password"
              name="smtp_password"
              type="password"
              value={configData.smtp_password}
              onChange={handleChange}
              required
              helperText="App password or account password"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="From Email"
              name="from_email"
              type="email"
              value={configData.from_email}
              onChange={handleChange}
              required
              helperText="Email address to send from"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="From Name"
              name="from_name"
              value={configData.from_name}
              onChange={handleChange}
              required
              helperText="Display name for outgoing emails"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={configData.use_tls}
                  onChange={handleChange}
                  name="use_tls"
                />
              }
              label="Use TLS/SSL encryption"
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                Save Configuration
              </Button>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={sendTestEmail}
                disabled={loading}
              >
                Send Test Email
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default EmailConfig;
